import { Alert, Button, Card, Col, Icon, SettingRow, Stack, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { say } from "../localization/say.ts";
import { cardStands, dismissTheCard, type Stance, stanceHere } from "./stance.ts";
import { subscribedHere, turnOffNotifications, turnOnNotifications, whyItDidNot } from "./subscribe.ts";

function useStance(): [Stance, () => void] {
  const [stance, setStance] = useState<Stance>(() => stanceHere());
  const again = useCallback(() => setStance(stanceHere()), []);
  return [stance, again];
}

export function NotificationsCard() {
  const theme = useTheme();
  const [stance, again] = useStance();
  const [asking, setAsking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  if (!cardStands(stance)) return null;

  const turnOn = async (): Promise<void> => {
    setAsking(true);
    const said = await turnOnNotifications();
    setAsking(false);
    setFailed(whyItDidNot(said));
    again();
  };

  return (
    <Col testID="push-card">
      <Card variant="inset" style={{ padding: theme.space[3] }} padded={false}>
        <Stack gap={2}>
          <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
            <Icon name="bell" size={16} color="accent" />
            <Text typeName="label">{say("push.cardTitle")}</Text>
          </Col>
          <Text typeName="caption" color="text.secondary">
            {say("push.cardWhy")}
          </Text>
          {failed ? (
            <Text typeName="caption" color="danger">
              {failed}
            </Text>
          ) : null}
          <Col style={{ flexDirection: "row", gap: theme.space[2], alignItems: "center" }}>
            <Button size="sm" loading={asking} testID="push-card-on" onPress={() => void turnOn()}>
              {say("push.turnOn")}
            </Button>
            <Tappable
              accessibilityRole="button"
              testID="push-card-dismiss"
              onPress={() => {
                dismissTheCard();
                again();
              }}
            >
              <Text typeName="caption" color="text.tertiary">
                {say("push.notNow")}
              </Text>
            </Tappable>
          </Col>
        </Stack>
      </Card>
    </Col>
  );
}

export function NotificationsSetting() {
  const theme = useTheme();
  const [stance, again] = useStance();
  const [on, setOn] = useState<boolean | null>(null);
  const [asking, setAsking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const ask = useCallback(() => {
    void subscribedHere().then(setOn);
  }, []);
  useEffect(ask, [ask]);

  const turnOn = async (): Promise<void> => {
    setAsking(true);
    const said = await turnOnNotifications();
    setAsking(false);
    setFailed(whyItDidNot(said));
    again();
    ask();
  };

  const turnOff = async (): Promise<void> => {
    setAsking(true);
    await turnOffNotifications().catch(() => undefined);
    setAsking(false);
    ask();
  };

  return (
    <Col>
      <SettingRow
        title={say("push.settingTitle")}
        note={stance === "granted" && on ? say("push.onHere") : say("push.cardWhy")}
        control={
          stance === "cannot" || stance === "denied" ? null : stance === "granted" && on ? (
            <Button
              size="sm"
              variant="soft"
              loading={asking}
              testID="push-off"
              onPress={() => void turnOff()}
            >
              {say("push.turnOff")}
            </Button>
          ) : (
            <Button size="sm" loading={asking} testID="push-on" onPress={() => void turnOn()}>
              {say("push.turnOn")}
            </Button>
          )
        }
      />
      {stance === "cannot" ? (
        <Col testID="push-cannot" style={{ paddingTop: theme.space[2] }}>
          <Alert tone="warning">{say("push.cannot")}</Alert>
        </Col>
      ) : stance === "denied" ? (
        <Col testID="push-denied" style={{ paddingTop: theme.space[2] }}>
          <Alert tone="warning">{say("push.denied")}</Alert>
        </Col>
      ) : null}
      {failed ? (
        <Text typeName="caption" color="danger">
          {failed}
        </Text>
      ) : null}
    </Col>
  );
}
